/**
 * iter86 (Item W) - Notification sound toggle.
 *
 * Founder brief:
 *   - "add a mute/notification-sound toggle in a lightweight settings area
 *      (reuse the account/dashboard settings area from the earlier placement
 *      fix). Default: sound ON. Persist the preference per user."
 *
 * This lives right above the DeleteAccountSection in the Dashboard footer -
 * one calm toggle, no separate settings page. Persists via PATCH
 * /api/notifications/preferences and broadcasts a `notification-prefs-updated`
 * DOM event so the NotificationBell picks up the new value without a remount.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Volume2, VolumeX } from "lucide-react";

export default function NotificationPrefsToggle() {
  const [enabled, setEnabled] = useState(true);
  const [ready,   setReady]   = useState(false);
  const [busy,    setBusy]    = useState(false);

  useEffect(() => {
    api.get("/notifications/preferences")
      .then(r => { setEnabled(r.data?.sound_enabled !== false); })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  const toggle = async () => {
    if (busy) return;
    const next = !enabled;
    setBusy(true);
    setEnabled(next);   // optimistic
    try {
      await api.patch("/notifications/preferences", { sound_enabled: next });
      // Tell the bell live, no remount.
      window.dispatchEvent(new CustomEvent("notification-prefs-updated",
        { detail: { sound_enabled: next } }));
      toast.success(next ? "Notification sound on" : "Notification sound off");
    } catch {
      setEnabled(!next);   // roll back
      toast.error("Couldn't save preference.");
    } finally {
      setBusy(false);
    }
  };

  if (!ready) return null;

  return (
    <div className="mt-8 pt-6 border-t border-ink/10" data-testid="dash-notif-prefs">
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold text-ink">
            Notification sound
          </div>
          <div className="mt-0.5 text-[12px] text-muted leading-snug max-w-lg">
            A short calm cue when a real notification arrives (new message, match
            accepted, someone joined your Assembly). Off is honoured immediately.
            {typeof navigator !== "undefined" && typeof navigator.vibrate !== "function" && (
              <> Vibration isn't supported on this device (iOS Safari doesn't allow it).</>
            )}
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={busy}
          data-testid="dash-notif-sound-toggle"
          aria-pressed={enabled}
          aria-label={enabled ? "Turn notification sound off" : "Turn notification sound on"}
          className={`relative inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-full transition-colors shrink-0 ${
            enabled
              ? "bg-[color:var(--moe-mint)] text-ink"
              : "bg-ink/10 text-muted"
          }`}
        >
          {enabled ? <Volume2 size={12}/> : <VolumeX size={12}/>}
          {enabled ? "On" : "Off"}
        </button>
      </div>
    </div>
  );
}
